import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import { T } from '../styles/theme';
import { useNotifiche } from '../hooks/useNotifiche';
import NotificheCampanella from './NotificheCampanella';

function UserAvatar({ user }) {
  if (user?.discord_id && user?.discord_avatar) {
    return <img src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.discord_avatar}.png`} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${T.border}` }} />;
  }
  const initial = (user?.name || '?').charAt(0).toUpperCase();
  return <div style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: T.border, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontWeight: 700, fontSize: '0.85rem', color: T.violet }}>{initial}</span></div>;
}

const icons = {
  dashboard: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  inventario: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>,
  annunci: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  analisi: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  best: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  fornitori: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 9h18v10a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/><path d="M3 9l2.45-4.9A2 2 0 017.24 3h9.52a2 2 0 011.8 1.1L21 9"/></svg>,
  vendite: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
  corso: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>,
  admin: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93l-1.41 1.41M5.34 18.66l-1.41 1.41M20 12h2M2 12h2M17.66 18.66l1.41 1.41M4.93 4.93l1.41 1.41"/></svg>,
  bozze: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  guida: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>,
  esci: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
};

const badgeMap = {
  admin: 'badge badge-amber',
  collaboratore: 'badge badge-green',
  pro: 'badge badge-violet',
  free: 'badge badge-red',
};

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { badge, notifiche, nonLette, segnaVista, segnaLetta, leggiTutte } = useNotifiche();

  useEffect(() => { api.get('/auth/me').then((r) => setUser(r.data)).catch(() => {}); }, []);

  const ruolo = user?.ruolo || 'free';
  const isCollab = ruolo === 'collaboratore';
  const displayName = user?.discord_username || user?.name || '';
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const handleLogout = () => { localStorage.removeItem('token'); localStorage.removeItem('impero_jwt_token'); navigate('/login'); };

  const menuItems = useMemo(() => [
    { to: '/dashboard', icon: icons.dashboard, label: 'Dashboard' },
    { to: '/products', icon: icons.inventario, label: 'Inventario' },
    { to: '/annunci', icon: icons.annunci, label: 'Annunci', sezione: 'annunci' },
    { to: '/bozze', icon: icons.bozze, label: 'Bozze' },
    { to: '/analisi-mercato', icon: icons.analisi, label: 'Analisi Mercato' },
    ...(['pro', 'admin', 'collaboratore'].includes(ruolo) ? [{ to: '/best-products', icon: icons.best, label: 'Best Product', sezione: 'best_product' }] : []),
    { to: '/fornitori', icon: icons.fornitori, label: 'Fornitori' },
    { to: '/vendite', icon: icons.vendite, label: 'Vendite' },
    { to: '/corso', icon: icons.corso, label: 'Corso' },
    { to: '/guida', icon: icons.guida, label: 'Guida' },
    ...(['admin', 'collaboratore'].includes(ruolo) ? [{ to: '/admin', icon: icons.admin, label: 'Admin' }] : []),
    { to: '/abbonamento', icon: icons.vendite, label: 'Abbonamento' },
  ], [ruolo]); // eslint-disable-line react-hooks/exhaustive-deps

  const sidebarContent = (
    <>
      {/* Logo + Campanella */}
      <div style={{ padding: '24px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/dashboard">
          <span style={{ fontFamily: T.fontDisplay, fontWeight: 700, fontSize: '1rem', background: 'linear-gradient(135deg,#a78bfa,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            IMPERO ELITE
          </span>
        </Link>
        <NotificheCampanella notifiche={notifiche} nonLette={nonLette} segnaLetta={segnaLetta} leggiTutte={leggiTutte} />
      </div>

      {/* User info */}
      {user && (
        <div style={{ padding: '0 12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: T.elem, border: `1px solid ${T.border}` }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <UserAvatar user={user} />
              <div style={{ position: 'absolute', bottom: -1, right: -1, width: 9, height: 9, borderRadius: '50%', background: '#4ade80', border: '2px solid #0d0b1e' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>{displayName}</p>
              <span style={{ fontSize: '0.58rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, letterSpacing: '0.03em', background: ruolo === 'admin' ? 'rgba(251,191,36,0.15)' : ruolo === 'collaboratore' ? 'rgba(74,222,128,0.15)' : ruolo === 'pro' ? 'rgba(167,139,250,0.15)' : 'rgba(248,113,113,0.15)', color: ruolo === 'admin' ? '#fbbf24' : ruolo === 'collaboratore' ? '#4ade80' : ruolo === 'pro' ? '#a78bfa' : '#f87171' }}>{ruolo === 'collaboratore' ? 'COLLAB' : ruolo.toUpperCase()}</span>
            </div>
            <button
              onClick={() => { navigate('/profilo'); setMobileOpen(false); }}
              title="Impostazioni profilo"
              style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer', transition: '0.2s' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(167,139,250,0.2)'; e.currentTarget.style.borderColor = '#7c3aed'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(167,139,250,0.08)'; e.currentTarget.style.borderColor = 'rgba(167,139,250,0.15)'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2"><path d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
          </div>
        </div>
      )}

      <div className="divider" style={{ margin: '0 16px 12px' }} />

      {/* Menu items */}
      <div style={{ flex: 1, paddingTop: 4 }}>
        {menuItems.map((item, idx) => {
          const active = isActive(item.to);
          return (
            <Link key={item.to} to={item.to} onClick={() => { setMobileOpen(false); if (item.sezione) segnaVista(item.sezione); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: active ? '9px 14px 9px 11px' : '9px 14px',
                margin: '1px 10px', borderRadius: 12, cursor: 'pointer',
                transition: '0.15s', fontFamily: T.fontBody, fontSize: '0.88rem',
                color: active ? T.violet : T.muted,
                background: active ? 'linear-gradient(90deg,rgba(124,58,237,0.18),transparent)' : 'transparent',
                borderLeft: active ? '3px solid #7c3aed' : '3px solid transparent',
                fontWeight: active ? 600 : 400,
                textDecoration: 'none',
                position: 'relative',
              }}
              onMouseEnter={(e) => { if (!active) { e.currentTarget.style.color = T.text; e.currentTarget.style.background = T.elem; } }}
              onMouseLeave={(e) => { if (!active) { e.currentTarget.style.color = T.muted; e.currentTarget.style.background = 'transparent'; } }}
            >
              <span style={{ position: 'relative' }}>
                {item.icon}
                {item.sezione && badge[item.sezione] > 0 && (
                  <span style={{
                    position: 'absolute', top: -6, right: -8,
                    width: 18, height: 18, borderRadius: '50%',
                    background: T.red, color: '#fff',
                    fontSize: '0.68rem', fontWeight: 700, fontFamily: T.fontBody,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    animation: 'badgePulse 2s ease-in-out infinite',
                    border: `2px solid ${T.bg}`,
                  }}>{badge[item.sezione] > 9 ? '9+' : badge[item.sezione]}</span>
                )}
              </span>
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>

      {/* Upgrade box for free users */}
      {user && !['admin', 'collaboratore', 'pro'].includes(ruolo) && (
        <div onClick={() => { navigate('/abbonamento'); setMobileOpen(false); }} style={{
          margin: '8px 12px', padding: '12px',
          background: 'linear-gradient(135deg,rgba(124,58,237,0.15),rgba(79,70,229,0.08))',
          border: '1px solid #7c3aed', borderRadius: 14,
          cursor: 'pointer', textAlign: 'center'
        }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#a78bfa', fontFamily: 'DM Sans,sans-serif' }}>⭐ Upgrade a PRO</div>
          <div style={{ fontSize: '0.65rem', color: '#6b7280', marginTop: 2 }}>Sblocca tutte le funzionalità</div>
        </div>
      )}

      {/* Online indicator */}
      <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="dot-live" />
        <span style={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.green }}>Online</span>
      </div>

      {/* Logout */}
      <div style={{ padding: '0 12px 16px' }}>
        <button onClick={handleLogout}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 14px', borderRadius: 12, cursor: 'pointer',
            transition: '0.15s', fontFamily: T.fontBody, fontSize: '0.88rem',
            color: T.red, background: 'transparent', border: 'none',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(248,113,113,0.08)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
          {icons.esci}
          <span>Esci</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden md:flex" style={{ position: 'fixed', left: 0, top: 0, width: 220, height: '100vh', backgroundColor: T.bg, borderRight: `1px solid ${T.border}`, flexDirection: 'column', zIndex: 100, overflowY: 'auto' }}>
        {sidebarContent}
      </div>

      {/* Mobile hamburger — hamburger LEFT, logo CENTER */}
      <div className="md:hidden" style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 52, display: 'flex', alignItems: 'center', padding: '0 16px', backgroundColor: T.bg, borderBottom: `1px solid ${T.border}`, zIndex: 200 }}>
        <button onClick={() => setMobileOpen(!mobileOpen)} style={{ color: T.muted, background: 'none', border: 'none', cursor: 'pointer', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileOpen ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
        </button>
        <Link to="/dashboard" style={{ flex: 1, textAlign: 'center', textDecoration: 'none' }}>
          <span style={{ fontFamily: T.fontDisplay, fontWeight: 700, fontSize: '1rem', background: 'linear-gradient(135deg,#a78bfa,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>IMPERO ELITE</span>
        </Link>
        <div style={{ width: 44, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <NotificheCampanella notifiche={notifiche} nonLette={nonLette} segnaLetta={segnaLetta} leggiTutte={leggiTutte} />
        </div>
      </div>

      <style>{`
        @keyframes badgePulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
      `}</style>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="md:hidden" style={{ position: 'fixed', inset: 0, zIndex: 99 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)' }} onClick={() => setMobileOpen(false)} />
          <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: 280, backgroundColor: T.bg, display: 'flex', flexDirection: 'column', overflowY: 'auto', WebkitOverflowScrolling: 'touch', boxShadow: '4px 0 24px rgba(0,0,0,0.4)' }}>
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  );
}
